import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import PersonalInformationPage from './PersonalInformationPage/PersonalInformationPage';
import { PersonalInformation } from './types/PersonalInformation';
import VelgMedlemskapPage from './VelgMedlemskapPage/VelgMedlemskapPage';
import { fetchMedlemskapstypeList, Medlemskapstype } from './api/medlemskapAPI';
import ReceiptPage from './ReceiptPage/ReceiptPage';
import VippsCheckoutPage from './VippsCheckoutPage/VippsCheckoutPage';
import EnkeltmedlemPersonalInformationPage from './PersonalInformationPage/EnkeltmedlemPersonalInformationPage';
import FamiliemedlemPersonalInformationPage from './PersonalInformationPage/FamilyMembership/FamiliemedlemPersonalInfomationPage';
import ErrorPage from './ErrorPage/ErrorPage';
import { ApiResource } from './api/ApiResource';
import styles from './App.module.scss';
import EditFamiliemedlemPage from './PersonalInformationPage/FamilyMembership/EditFamiliemedlemPage';
import EditBarnPage from './PersonalInformationPage/FamilyMembership/EditBarnPage';
import { getPostalCodes, PostalCodeResponse } from './api/postalCodeAPI';

function App() {
    const [medlemskapstypeListResource, setMedlemskapListResource] = useState<ApiResource<Medlemskapstype[]>>({
        status: 'not-loaded',
    });
    const [postalCodes, setPostalCodes] = useState<ApiResource<PostalCodeResponse>>({ status: 'not-loaded' });
    const [hovedmedlemInput, setHovedmedlemInput] = useState<PersonalInformation>({
        phoneNumber: '',
        firstname: '',
        lastname: '',
        email: '',
        address: '',
        postalcode: '',
        dateOfBirth: '',
        newsletter: false,
        freetext: '',
    });

    const [partnerInput, setPartnerInput] = useState<PersonalInformation>();
    const [barnInput, setBarnInput] = useState<PersonalInformation[]>();

    const [amountToPay, setAmountToPay] = useState(0);
    const [discountCode, setDiscountCode] = useState('');
    const [discount, setDiscount] = useState(0);

    useEffect(() => {
        fetchMedlemskapstypeList().then((response) => {
            setMedlemskapListResource(response);
        });
    }, []);

    useEffect(() => {
        getPostalCodes().then((response) => {
            setPostalCodes(response);
        });
    }, []);

    const basename = selectEnglishOrNorwegianBasename();

    return (
        <BrowserRouter basename={basename}>
            <main className={styles.App}>
                <Routes>
                    <Route
                        path="/"
                        element={<VelgMedlemskapPage medlemskapstypeListResource={medlemskapstypeListResource} />}
                    />
                    <Route
                        path="/personalia/Enkelt"
                        element={
                            <EnkeltmedlemPersonalInformationPage
                                postalCodesListResource={postalCodes}
                                medlemskapstypeListResource={medlemskapstypeListResource}
                                formInput={hovedmedlemInput}
                                setFormInput={setHovedmedlemInput}
                                setAmountToPay={setAmountToPay}
                                discountCode={discountCode}
                                setDiscountCode={setDiscountCode}
                                discount={discount}
                                setDiscount={setDiscount}
                            />
                        }
                    />
                    <Route path="/personalia/Familie">
                        <Route
                            path=""
                            element={
                                <FamiliemedlemPersonalInformationPage
                                    postalCodesListResource={postalCodes}
                                    medlemskapstypeListResource={medlemskapstypeListResource}
                                    memberInput={{
                                        hovedmedlem: hovedmedlemInput,
                                        partner: partnerInput,
                                        barn: barnInput,
                                    }}
                                    setHovedmedlemInput={setHovedmedlemInput}
                                    setPartnerInput={setPartnerInput}
                                    setBarnInput={setBarnInput}
                                    setAmountToPay={setAmountToPay}
                                    discountCode={discountCode}
                                    setDiscountCode={setDiscountCode}
                                    discount={discount}
                                    setDiscount={setDiscount}
                                />
                            }
                        />
                        <Route
                            path="hoved"
                            element={
                                <EditFamiliemedlemPage
                                    postalCodesListResource={postalCodes}
                                    formInput={hovedmedlemInput}
                                    setFormInput={setHovedmedlemInput}
                                    isHovedmedlem={true}
                                />
                            }
                        />
                        <Route
                            path="partner"
                            element={
                                <EditFamiliemedlemPage
                                    postalCodesListResource={postalCodes}
                                    formInput={
                                        partnerInput ?? {
                                            phoneNumber: '',
                                            firstname: '',
                                            lastname: '',
                                            email: '',
                                            address: '',
                                            postalcode: '',
                                            dateOfBirth: '',
                                            newsletter: false,
                                        }
                                    }
                                    setFormInput={setPartnerInput}
                                    isHovedmedlem={false}
                                />
                            }
                        />
                        <Route
                            path="barn/:id"
                            element={
                                <EditBarnPage
                                    postalCodesListResource={postalCodes}
                                    barn={
                                        barnInput ?? [
                                            {
                                                phoneNumber: '',
                                                firstname: '',
                                                lastname: '',
                                                email: '',
                                                address: '',
                                                postalcode: '',
                                                dateOfBirth: '',
                                                newsletter: false,
                                            },
                                        ]
                                    }
                                    setBarn={setBarnInput}
                                />
                            }
                        />
                    </Route>
                    <Route
                        path="/personalia/:id"
                        element={
                            <PersonalInformationPage
                                postalCodesListResource={postalCodes}
                                medlemskapstypeListResource={medlemskapstypeListResource}
                                formInput={hovedmedlemInput}
                                setFormInput={setHovedmedlemInput}
                                setAmountToPay={setAmountToPay}
                                discountCode={discountCode}
                                setDiscountCode={setDiscountCode}
                                discount={discount}
                                setDiscount={setDiscount}
                            />
                        }
                    />
                    {/* should be a protected and conditional path */}
                    <Route
                        path="/betaling/:id"
                        element={
                            <VippsCheckoutPage
                                medlemskapstypeListResource={medlemskapstypeListResource}
                                memberInput={{
                                    hovedmedlem: hovedmedlemInput,
                                    partner: partnerInput,
                                    barn: barnInput,
                                }}
                                amountToPay={amountToPay}
                                discountCode={discountCode}
                                discount={discount}
                            />
                        }
                    />
                    <Route path="/kvittering/:reference" element={<ReceiptPage />} />
                    <Route path="/feil" element={<ErrorPage />} />
                </Routes>
            </main>
        </BrowserRouter>
    );
}

function selectEnglishOrNorwegianBasename() {
    const url = window.location.href;
    if (url != null) {
        return window.location.pathname.startsWith('/en/bli-medlem') ? 'en/bli-medlem' : 'bli-medlem';
    }
    return 'bli-medlem';
}

export default App;
